import { getStoredToken } from './auth'
import { getFileNameFromContentDisposition } from '@/utils/contentDisposition'

const API_BASE_URL = import.meta.env.VITE_DAPI_URL || ''

export interface MoneyAmount {
  value: number
  currency: 'SEK'
}

export interface InvoiceListItem {
  id: string
  dueDate: string
  amounts: {
    invoiced: MoneyAmount
    paid: MoneyAmount
    remaining: MoneyAmount
  }
}

export interface InvoiceReminder {
  createdDate: string
  fileId: string
}

export interface InvoiceDetail {
  id: string
  invoiceNumber: string
  dueDate: string
  paymentMethod: string
  fileId: string | null
  reminders: InvoiceReminder[]
  amounts: {
    invoiced: MoneyAmount
    paid: MoneyAmount
    remaining: MoneyAmount
    credited: MoneyAmount
  }
}

const getAuthorizationHeader = () => {
  const token = getStoredToken()

  if (!token || !('jwt' in token) || !token.jwt) {
    throw new Error('Missing authentication token.')
  }

  return { authorization: `bearer ${token.jwt}` }
}

export const fetchInvoicesByUser = async (userId: string): Promise<InvoiceListItem[]> => {
  const params = new URLSearchParams({ userId })
  const response = await fetch(`${API_BASE_URL}/borgo/distributor/invoices?${params}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...getAuthorizationHeader(),
    },
  })

  if (!response.ok) {
    throw new Error('Kunde inte hämta fakturor')
  }

  return await response.json()
}

export const fetchInvoiceById = async (id: string): Promise<InvoiceDetail> => {
  const response = await fetch(`${API_BASE_URL}/borgo/distributor/invoices/${encodeURIComponent(id)}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...getAuthorizationHeader(),
    },
  })

  if (!response.ok) {
    throw new Error(`Kunde inte hämta faktura ${id}`)
  }

  return await response.json()
}

export interface InvoiceFile {
  blob: Blob
  contentType: string
  fileName?: string
}

export const fetchInvoiceFile = async (invoiceId: string, fileId: string): Promise<InvoiceFile> => {
  const response = await fetch(
    `${API_BASE_URL}/borgo/distributor/invoices/${encodeURIComponent(invoiceId)}/download/${encodeURIComponent(fileId)}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: getAuthorizationHeader(),
    },
  )

  if (!response.ok) {
    throw new Error('Kunde inte hämta fakturafilen')
  }

  const blob = await response.blob()

  return {
    blob,
    contentType: response.headers.get('content-type') ?? blob.type,
    fileName: getFileNameFromContentDisposition(response.headers.get('content-disposition')),
  }
}
